import React, { useState } from 'react';
import axios from 'axios';

const MessagesCard = ({ conversations, setConversations, unreadCount, setUnreadCount, user, API_BASE }) => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [messengerUsers, setMessengerUsers] = useState([]);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Get recent conversations (top 4)
  const recentConversations = conversations.slice(0, 4);

  const getConversationName = (conversation) => {
    if (conversation.name) return conversation.name;
    if (conversation.participants) {
      const otherParticipants = conversation.participants.filter(p => p.id !== user?.id);
      if (otherParticipants.length > 0) {
        return otherParticipants.map(p => p.display_name || p.username).join(', ');
      }
    }
    return 'Unknown';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleOpenChat = async (conversation) => {
    setSelectedConversation(conversation);
    setShowChatModal(true);

    // Fetch messages
    try {
      const response = await axios.get(
        `${API_BASE}/messenger/conversations/${conversation.id}/messages`,
        { withCredentials: true }
      );
      setMessages(response.data);

      // Mark as read
      await axios.patch(
        `${API_BASE}/messenger/conversations/${conversation.id}/read`,
        {},
        { withCredentials: true }
      );

      // Update conversations
      const updatedConversations = conversations.map(c =>
        c.id === conversation.id ? { ...c, unread_count: 0 } : c
      );
      setConversations(updatedConversations);

      // Update unread count
      const unreadRes = await axios.get(`${API_BASE}/messenger/unread-count`, { withCredentials: true });
      setUnreadCount(unreadRes.data?.unread_count || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;

    try {
      await axios.post(
        `${API_BASE}/messenger/conversations/${selectedConversation.id}/messages`,
        { content: messageInput },
        { withCredentials: true }
      );
      setMessageInput('');

      // Refresh messages
      const response = await axios.get(
        `${API_BASE}/messenger/conversations/${selectedConversation.id}/messages`,
        { withCredentials: true }
      );
      setMessages(response.data);

      // Refresh conversations
      const convRes = await axios.get(`${API_BASE}/messenger/conversations`, { withCredentials: true });
      setConversations(convRes.data || []);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleStartNewConversation = async () => {
    if (!selectedUserId) return;

    try {
      const response = await axios.post(
        `${API_BASE}/messenger/conversations`,
        { participant_ids: [parseInt(selectedUserId)], is_group: false },
        { withCredentials: true }
      );
      setShowNewConversation(false);
      setSelectedUserId('');
      handleOpenChat(response.data);

      // Refresh conversations
      const convRes = await axios.get(`${API_BASE}/messenger/conversations`, { withCredentials: true });
      setConversations(convRes.data || []);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const fetchMessengerUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE}/messenger/users`, { withCredentials: true });
      setMessengerUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  return (
    <div className="desk-card messages-card">
      <div className="desk-card-header">
        <div className="desk-card-header-left">
          <div className="desk-card-icon messages">
            <span>&#128172;</span>
          </div>
          <span className="desk-card-title">Messages</span>
          {unreadCount > 0 && (
            <span style={{
              background: '#5a67d8',
              color: 'white',
              fontSize: '0.65rem',
              padding: '0.1rem 0.4rem',
              borderRadius: '10px',
              marginLeft: '0.5rem'
            }}>
              {unreadCount}
            </span>
          )}
        </div>
        <button
          className="desk-card-action"
          onClick={() => {
            fetchMessengerUsers();
            setShowNewConversation(true);
            setShowChatModal(true);
          }}
        >
          + New
        </button>
      </div>
      <div className="desk-card-body">
        {recentConversations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#a0aec0', fontSize: '0.85rem' }}>
            No conversations yet
          </div>
        ) : (
          recentConversations.map(conversation => (
            <div
              key={conversation.id}
              className={`message-item ${conversation.unread_count > 0 ? 'unread' : ''}`}
              onClick={() => handleOpenChat(conversation)}
            >
              <div className="message-avatar">
                {getConversationName(conversation).charAt(0).toUpperCase()}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-sender">{getConversationName(conversation)}</span>
                  <span className="message-time">
                    {conversation.last_message && formatTime(conversation.last_message.created_at)}
                  </span>
                </div>
                <div className="message-preview">
                  {conversation.last_message?.content || 'No messages yet'}
                </div>
              </div>
              {conversation.unread_count > 0 && <div className="unread-dot"></div>}
            </div>
          ))
        )}
      </div>

      {/* Chat Modal */}
      {showChatModal && (
        <div className="modal-overlay" onClick={() => { setShowChatModal(false); setShowNewConversation(false); }}>
          <div className="modal-content" style={{ maxWidth: '500px', height: '500px', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {showNewConversation
                  ? 'New Conversation'
                  : selectedConversation
                    ? getConversationName(selectedConversation)
                    : 'Messages'}
              </h3>
              <button className="modal-close" onClick={() => { setShowChatModal(false); setShowNewConversation(false); }}>×</button>
            </div>

            {showNewConversation ? (
              <div className="modal-body">
                <div className="form-group">
                  <label>Select User</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem' }}
                  >
                    <option value="">Choose a user...</option>
                    {messengerUsers.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.display_name || u.username}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn-create"
                  onClick={handleStartNewConversation}
                  style={{ marginTop: '1rem' }}
                >
                  Start Conversation
                </button>
              </div>
            ) : selectedConversation ? (
              <>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', background: '#f7fafc' }}>
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        justifyContent: msg.sender_id === user?.id ? 'flex-end' : 'flex-start',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '12px',
                          background: msg.sender_id === user?.id ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                          color: msg.sender_id === user?.id ? 'white' : '#2d3748',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                      >
                        <div style={{ fontSize: '0.85rem' }}>{msg.content}</div>
                        <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '0.25rem' }}>
                          {formatTime(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '1rem', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type a message..."
                    style={{ flex: 1, padding: '0.5rem 0.75rem', borderRadius: '20px', border: '1px solid #e2e8f0' }}
                  />
                  <button
                    onClick={handleSendMessage}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      cursor: 'pointer'
                    }}
                  >
                    Send
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesCard;
